// FUDR-13118: flow-aware inactivity reset (pure helpers — no listeners / Redux)

import { get, isEmpty } from 'lodash';

import routes from '../routes';
import orderingRoutes from '../routes/ordering';
import rewardsRoutes from '../routes/rewards';
import siteRoutes from '../routes/site';

export const inactivityFlows = {
  rewards: 'rewards',
  ordering: 'ordering',
  restaurant: 'restaurant',
};

export const inactivityResetSkipReasons = {
  noFlow: 'no-flow',
  excludedRoute: 'excluded-route',
  missingRestaurantContext: 'missing-restaurant-context',
  alreadyAtDestination: 'already-at-destination',
};

const DEPLOY_BASENAME = 'fudr-web-client';

const RESTAURANT_FLOW_ROOTS = [
  routes.containers.scan,
  routes.containers.restaurant,
  routes.containers.cart,
  routes.containers.confirmation,
  routes.containers.payment,
  routes.containers.orders,
  routes.containers.account,
  routes.containers.welcome,
  routes.containers.offers,
  routes.containers.aboutOutlet,
  routes.containers.cashCards,
];

const EXCLUDED_RESET_SEGMENTS = [routes.containers.payment, routes.containers.confirmation];

export const getNormalizedPathSegments = (pathname = '') => {
  const safePathname = typeof pathname === 'string' ? pathname : '';
  const segments = safePathname.split('/').filter(Boolean);
  if (segments[0] === DEPLOY_BASENAME) {
    return segments.slice(1);
  }
  return segments;
};

export const resolveInactivityFlow = (pathname = '') => {
  const segments = getNormalizedPathSegments(pathname);
  const root = segments[0] || '';

  if (root === rewardsRoutes.rewards) {
    return inactivityFlows.rewards;
  }
  if (root === orderingRoutes.ordering) {
    return inactivityFlows.ordering;
  }
  if (root === siteRoutes.site) {
    return null;
  }
  if (RESTAURANT_FLOW_ROOTS.indexOf(root) !== -1) {
    return inactivityFlows.restaurant;
  }
  return null;
};

export const isExcludedInactivityResetPath = (pathname = '') => {
  const segments = getNormalizedPathSegments(pathname);
  return EXCLUDED_RESET_SEGMENTS.some((segment) => segments.indexOf(segment) !== -1);
};

export const getRestaurantCodeFromPath = (pathname = '', flow) => {
  const segments = getNormalizedPathSegments(pathname);

  if (flow === inactivityFlows.ordering) {
    const welcomeIdx = segments.indexOf(orderingRoutes.containers.welcome);
    if (welcomeIdx !== -1 && segments[welcomeIdx + 1]) {
      return segments[welcomeIdx + 1];
    }
    const menuIdx = segments.indexOf(orderingRoutes.containers.menu);
    if (menuIdx !== -1 && segments[menuIdx + 1]) {
      return segments[menuIdx + 1];
    }
    return '';
  }

  if (flow === inactivityFlows.restaurant) {
    const restaurantIdx = segments.indexOf(routes.containers.restaurant);
    if (restaurantIdx !== -1 && segments[restaurantIdx + 1]) {
      return segments[restaurantIdx + 1];
    }
    if (segments[0] === routes.containers.welcome && segments[1]) {
      return segments[1];
    }
  }

  return '';
};

export const getRestaurantCodeForFlow = (flow, state, pathname = '') => {
  if (flow === inactivityFlows.ordering) {
    return (
      get(state, 'ordering.restaurant.restaurantCode', '') ||
      get(state, 'ordering.selectedTable.scanCode', '') ||
      getRestaurantCodeFromPath(pathname, flow)
    );
  }

  if (flow === inactivityFlows.restaurant) {
    return (
      get(state, 'app.restaurant.restaurantCode', '') ||
      get(state, 'app.selectedTable.scanCode', '') ||
      getRestaurantCodeFromPath(pathname, flow)
    );
  }

  return '';
};

export const getInactivityResetDestination = (flow, restaurantCode = '') => {
  if (flow === inactivityFlows.rewards) {
    return `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.home}`;
  }
  if (flow === inactivityFlows.ordering && !isEmpty(restaurantCode)) {
    return `/${orderingRoutes.ordering}/${orderingRoutes.containers.welcome}/${restaurantCode}`;
  }
  if (flow === inactivityFlows.restaurant && !isEmpty(restaurantCode)) {
    return `/${routes.containers.welcome}/${restaurantCode}`;
  }
  return '';
};

export const isAtInactivityResetDestination = (pathname = '', flow, restaurantCode = '') => {
  const destination = getInactivityResetDestination(flow, restaurantCode);
  if (isEmpty(destination)) {
    return false;
  }
  const current = `/${getNormalizedPathSegments(pathname).join('/')}`;
  return current === destination;
};

export const getInactivityDurationMs = (lastActiveAt, now = Date.now()) => {
  const lastActive = Number(lastActiveAt);
  if (!Number.isFinite(lastActive) || lastActive <= 0) {
    return 0;
  }
  return Math.max(0, now - lastActive);
};

export const isInactivityThresholdExceeded = (lastActiveAt, thresholdMins, now = Date.now()) => {
  const mins = Number(thresholdMins);
  if (!Number.isFinite(mins) || mins <= 0) {
    return false;
  }
  const lastActive = Number(lastActiveAt);
  if (!Number.isFinite(lastActive) || lastActive <= 0) {
    return false;
  }
  return getInactivityDurationMs(lastActive, now) >= mins * 60 * 1000;
};

/**
 * Decide whether a flow-aware reset should run for the current location + store.
 * Does not compare inactivity duration — callers gate on the threshold first.
 */
export const buildInactivityResetPlan = ({ pathname = '', state = {} } = {}) => {
  if (isExcludedInactivityResetPath(pathname)) {
    return { apply: false, reason: inactivityResetSkipReasons.excludedRoute };
  }

  const flow = resolveInactivityFlow(pathname);
  if (!flow) {
    return { apply: false, reason: inactivityResetSkipReasons.noFlow };
  }

  const restaurantCode = getRestaurantCodeForFlow(flow, state, pathname);
  const needsRestaurantContext =
    flow === inactivityFlows.ordering || flow === inactivityFlows.restaurant;

  if (needsRestaurantContext && isEmpty(restaurantCode)) {
    return { apply: false, reason: inactivityResetSkipReasons.missingRestaurantContext, flow };
  }

  if (isAtInactivityResetDestination(pathname, flow, restaurantCode)) {
    return {
      apply: false,
      reason: inactivityResetSkipReasons.alreadyAtDestination,
      flow,
      restaurantCode,
    };
  }

  return {
    apply: true,
    flow,
    restaurantCode,
    redirectTo: getInactivityResetDestination(flow, restaurantCode),
  };
};
